import React, { useState, useEffect, useRef } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Box,
    Typography,
    TextField,
    Button,
    IconButton,
    Avatar,
    Chip,
    CircularProgress,
    Paper,
    Divider,
    useTheme,
    useMediaQuery,
    Fade
} from '@mui/material';
import {
    Send,
    Close,
    AttachFile,
    EmojiEmotions,
    MoreVert,
    Check,
    DoneAll,
    Schedule,
    Error as ErrorIcon
} from '@mui/icons-material';
import { motion, AnimatePresence } from 'framer-motion';
import { useDispatch, useSelector } from 'react-redux';
import P2PChatService from '../../services/p2pChat.service';
import apiClient from '../../request/http-common';
import {
    updateChatSession,
    addChatMessage,
    markChatAsRead,
    setError
} from '../../request/P2P/p2pSlice';

const P2PChatDialog = ({ open, onClose, order }) => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    const dispatch = useDispatch();
    
    const [messages, setMessages] = useState([]);
    const [newMessage, setNewMessage] = useState('');
    const [loading, setLoading] = useState(false);
    const [sending, setSending] = useState(false);
    const [error, setErrorState] = useState(null);
    
    const messagesEndRef = useRef(null);
    const chatPollingRef = useRef(null);
    
    const currentUser = useSelector((state) => state.user?.user);
    const chatSessions = useSelector((state) => state.p2p?.chatSessions || {});

    // Get cached messages from Redux
    const cachedSession = order ? chatSessions[order.order_id] : null;

    useEffect(() => {
        if (open && order) {
            initializeChat();
            startPolling();
            
            // Mark as read when dialog opens
            dispatch(markChatAsRead(order.order_id));
        }

        return () => {
            stopPolling();
        };
    }, [open, order]);

    useEffect(() => {
        scrollToBottom();
    }, [messages]);

    // Load cached messages first, then fetch fresh ones
    useEffect(() => {
        if (cachedSession?.messages) {
            setMessages(cachedSession.messages);
        }
    }, [cachedSession]);

    const initializeChat = async () => {
        if (!order) return;

        try {
            setLoading(true);
            setErrorState(null);
            
            // Initialize chat session
            await P2PChatService.initializeChat(order.order_id);
            
            // Load messages
            await fetchMessages();
        } catch (error) {
            console.error('Failed to initialize chat:', error);
            setErrorState('Failed to load chat. Please try again.');
            dispatch(setError('Failed to initialize chat'));
        } finally {
            setLoading(false);
        }
    };

    const fetchMessages = async () => {
        if (!order) return;

        try {
            const response = await P2PChatService.getChatMessages(order.order_id);
            
            if (response.success && response.messages) {
                const formattedMessages = response.messages.map(msg => ({
                    id: msg.id,
                    message: msg.message,
                    sender_id: msg.sender_id,
                    sender_type: msg.sender_type,
                    is_from_current_user: msg.sender_id === currentUser?.id,
                    created_at: msg.created_at,
                    status: 'delivered',
                    message_type: msg.message_type || 'text',
                    file_url: msg.file_url
                }));

                setMessages(formattedMessages);
                
                // Update Redux cache
                dispatch(updateChatSession({
                    orderId: order.order_id,
                    messages: formattedMessages,
                    lastMessage: formattedMessages[formattedMessages.length - 1],
                    unreadCount: 0
                }));
            }
        } catch (error) {
            console.error('Failed to fetch messages:', error);
            if (error.response?.status === 401) {
                setErrorState('Authentication required. Please login again.');
            } else {
                setErrorState('Failed to load messages.');
            }
        }
    };

    const sendMessage = async () => {
        if (!newMessage.trim() || !order || sending) return;

        const messageText = newMessage.trim();
        setNewMessage('');
        setSending(true);

        // Optimistic update
        const tempMessage = {
            id: `temp-${Date.now()}`,
            message: messageText,
            sender_id: currentUser?.id,
            sender_type: 'user',
            is_from_current_user: true,
            created_at: new Date().toISOString(),
            status: 'sending',
            message_type: 'text'
        };

        setMessages(prev => [...prev, tempMessage]);

        try {
            const response = await P2PChatService.sendMessage(order.order_id, messageText);
            
            if (response.success) {
                // Replace temp message with server response
                setMessages(prev => prev.map(msg => 
                    msg.id === tempMessage.id 
                        ? { ...response.message, is_from_current_user: true, status: 'delivered' }
                        : msg
                ));

                // Update Redux cache
                dispatch(addChatMessage({
                    orderId: order.order_id,
                    message: { ...response.message, is_from_current_user: true }
                }));

                // Refresh messages to get any new ones
                setTimeout(() => fetchMessages(), 500);
            } else {
                throw new Error(response.message || 'Failed to send message');
            }
        } catch (error) {
            console.error('Failed to send message:', error);
            
            // Update message status to error
            setMessages(prev => prev.map(msg => 
                msg.id === tempMessage.id 
                    ? { ...msg, status: 'error' }
                    : msg
            ));

            if (error.response?.status === 401) {
                setErrorState('Authentication required. Please login again.');
            } else {
                setErrorState('Failed to send message. Please try again.');
            }
        } finally {
            setSending(false);
        }
    };

    const handleAttachImage = async (event) => {
        const file = event.target.files?.[0];
        if (!file || !order) return;
        setSending(true);
        try {
            const temp = {
                id: `temp-file-${Date.now()}`,
                message: '[image uploading...]',
                sender_id: currentUser?.id,
                is_from_current_user: true,
                created_at: new Date().toISOString(),
                status: 'sending',
                message_type: 'image'
            };
            setMessages(prev => [...prev, temp]);

            const res = await P2PChatService.sendImage(order.order_id, file);
            if (res?.success) {
                setMessages(prev => prev.map(m => m.id === temp.id ? { ...res.message, is_from_current_user: true, status: 'delivered' } : m));
                dispatch(addChatMessage({ orderId: order.order_id, message: { ...res.message, is_from_current_user: true } }));
                setTimeout(() => fetchMessages(), 500);
            } else {
                setMessages(prev => prev.map(m => m.id === temp.id ? { ...m, status: 'error', message: 'image upload failed' } : m));
            }
        } catch (e) {
            setMessages(prev => prev.map(m => m.id === `temp-file-${Date.now()}` ? { ...m, status: 'error' } : m));
        } finally {
            setSending(false);
            event.target.value = '';
        }
    };

    const startPolling = () => {
        if (chatPollingRef.current) return;

        chatPollingRef.current = setInterval(() => {
            if (order && open) {
                fetchMessages();
            }
        }, 5000); // Poll every 5 seconds
    };

    const stopPolling = () => {
        if (chatPollingRef.current) {
            clearInterval(chatPollingRef.current);
            chatPollingRef.current = null;
        }
    };

    const scrollToBottom = () => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    };

    const handleKeyPress = (e) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            sendMessage();
        }
    };

    const formatTime = (timestamp) => {
        return new Date(timestamp).toLocaleTimeString([], { 
            hour: '2-digit', 
            minute: '2-digit' 
        });
    };

    const formatDate = (timestamp) => {
        const date = new Date(timestamp);
        const today = new Date();
        const yesterday = new Date(today);
        yesterday.setDate(yesterday.getDate() - 1);

        if (date.toDateString() === today.toDateString()) {
            return 'Today';
        } else if (date.toDateString() === yesterday.toDateString()) {
            return 'Yesterday';
        } else {
            return date.toLocaleDateString();
        }
    };

    const renderMessageStatus = (message) => {
        if (!message.is_from_current_user) return null;

        switch (message.status) {
            case 'sending':
                return <Schedule sx={{ fontSize: 14, opacity: 0.5 }} />;
            case 'delivered':
                return <Check sx={{ fontSize: 14, opacity: 0.7 }} />;
            case 'read':
                return <DoneAll sx={{ fontSize: 14, color: 'primary.main' }} />;
            case 'error':
                return <ErrorIcon sx={{ fontSize: 14, color: 'error.main' }} />;
            default:
                return null;
        }
    };

    const groupMessagesByDate = (messages) => {
        const groups = {};
        messages.forEach(message => {
            const date = formatDate(message.created_at);
            if (!groups[date]) {
                groups[date] = [];
            }
            groups[date].push(message);
        });
        return groups;
    };

    const messageGroups = groupMessagesByDate(messages);

    const isLikelyUrl = (text) => {
        if (typeof text !== 'string') return false;
        return /^https?:\/\//i.test(text) || text.startsWith('/');
    };

    const isImagePath = (text) => {
        if (typeof text !== 'string') return false;
        return /(\.png|\.jpg|\.jpeg|\.gif|\.webp)(\?.*)?$/i.test(text);
    };

    const toAbsoluteUrl = (url) => {
        if (!url) return '';
        if (/^https?:\/\//i.test(url)) return url;
        const base = apiClient?.defaults?.baseURL?.replace(/\/$/, '') || '';
        return `${base}/${url.replace(/^\/+/, '')}`;
    };

    if (!order) return null;

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="md"
            fullWidth
            fullScreen={isMobile}
            sx={{ zIndex: 12000 }}
            BackdropProps={{ sx: { zIndex: 11999 } }}
            slotProps={{ backdrop: { sx: { zIndex: 11999 } }, root: { sx: { zIndex: 12000 } } }}
            PaperProps={{
                sx: {
                    zIndex: 12001,
                    height: isMobile ? '100vh' : '600px',
                    display: 'flex',
                    flexDirection: 'column'
                }
            }}
        >
            {/* Header */}
            <DialogTitle
                sx={{
                    background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`,
                    color: 'white',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    py: 2
                }}
            >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Avatar sx={{ bgcolor: 'rgba(255,255,255,0.2)' }}>
                        {order.customer?.name?.charAt(0)?.toUpperCase() || 'U'}
                    </Avatar>
                    <Box>
                        <Typography variant="h6" sx={{ fontWeight: 600 }}>
                            {order.customer?.name || 'Customer'}
                        </Typography>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Typography variant="body2" sx={{ opacity: 0.9 }}>
                                Order #{order.order_id}
                            </Typography>
                            <Chip
                                label={order.status.replace('_', ' ').toUpperCase()}
                                size="small"
                                sx={{
                                    bgcolor: 'rgba(255,255,255,0.2)',
                                    color: 'white',
                                    fontSize: '0.7rem'
                                }}
                            />
                        </Box>
                    </Box>
                </Box>
                <IconButton
                    onClick={onClose}
                    sx={{ color: 'white' }}
                    size="small"
                >
                    <Close />
                </IconButton>
            </DialogTitle>

            {/* Messages Area */}
            <DialogContent
                sx={{
                    flex: 1,
                    display: 'flex',
                    flexDirection: 'column',
                    p: 0,
                    bgcolor: theme.palette.mode === 'dark' ? 'grey.900' : 'grey.50'
                }}
            >
                <Box
                    sx={{
                        flex: 1,
                        overflowY: 'auto',
                        p: 2,
                        display: 'flex',
                        flexDirection: 'column'
                    }}
                >
                    {loading && messages.length === 0 ? (
                        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                            <CircularProgress size={24} />
                        </Box>
                    ) : error ? (
                        <Box sx={{ textAlign: 'center', py: 4 }}>
                            <Typography color="error" gutterBottom>
                                {error}
                            </Typography>
                            <Button
                                variant="outlined"
                                onClick={() => {
                                    setErrorState(null);
                                    initializeChat();
                                }}
                            >
                                Retry
                            </Button>
                        </Box>
                    ) : Object.keys(messageGroups).length === 0 ? (
                        <Box sx={{ textAlign: 'center', py: 4 }}>
                            <Typography variant="body2" color="text.secondary">
                                No messages yet. Start the conversation!
                            </Typography>
                        </Box>
                    ) : (
                        Object.entries(messageGroups).map(([date, dateMessages]) => (
                            <Box key={date} sx={{ mb: 2 }}>
                                {/* Date Divider */}
                                <Box sx={{ display: 'flex', alignItems: 'center', my: 2 }}>
                                    <Divider sx={{ flex: 1 }} />
                                    <Typography
                                        variant="caption"
                                        sx={{
                                            mx: 2,
                                            px: 2,
                                            py: 0.5,
                                            bgcolor: 'background.paper',
                                            borderRadius: 1,
                                            color: 'text.secondary'
                                        }}
                                    >
                                        {date}
                                    </Typography>
                                    <Divider sx={{ flex: 1 }} />
                                </Box>

                                {/* Messages */}
                                <AnimatePresence>
                                    {dateMessages.map((message, index) => (
                                        <motion.div
                                            key={message.id}
                                            initial={{ opacity: 0, y: 20 }}
                                            animate={{ opacity: 1, y: 0 }}
                                            exit={{ opacity: 0, y: -20 }}
                                            transition={{ duration: 0.3 }}
                                        >
                                            <Box
                                                sx={{
                                                    display: 'flex',
                                                    justifyContent: message.is_from_current_user ? 'flex-end' : 'flex-start',
                                                    mb: 1
                                                }}
                                            >
                                                <Paper
                                                    elevation={1}
                                                    sx={{
                                                        maxWidth: '70%',
                                                        p: 1.5,
                                                        bgcolor: message.is_from_current_user
                                                            ? 'primary.main'
                                                            : 'background.paper',
                                                        color: message.is_from_current_user
                                                            ? 'primary.contrastText'
                                                            : 'text.primary',
                                                        borderRadius: 2,
                                                        borderTopRightRadius: message.is_from_current_user ? 4 : 16,
                                                        borderTopLeftRadius: message.is_from_current_user ? 16 : 4,
                                                        opacity: message.status === 'sending' ? 0.7 : 1,
                                                        border: message.status === 'error' ? '1px solid' : 'none',
                                                        borderColor: message.status === 'error' ? 'error.main' : 'transparent'
                                                    }}
                                                >
                                                    {isLikelyUrl(message.message) && isImagePath(message.message) ? (
                                                        <img
                                                            src={toAbsoluteUrl(message.message)}
                                                            alt="chat-attachment"
                                                            style={{ maxWidth: '100%', borderRadius: 8, display: 'block' }}
                                                        />
                                                    ) : (
                                                        <Typography variant="body2">
                                                            {message.message}
                                                        </Typography>
                                                    )}
                                                    <Box
                                                        sx={{
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            justifyContent: 'flex-end',
                                                            gap: 0.5,
                                                            mt: 0.5
                                                        }}
                                                    >
                                                        <Typography
                                                            variant="caption"
                                                            sx={{
                                                                opacity: 0.7,
                                                                fontSize: '0.7rem'
                                                            }}
                                                        >
                                                            {formatTime(message.created_at)}
                                                        </Typography>
                                                        {renderMessageStatus(message)}
                                                    </Box>
                                                </Paper>
                                            </Box>
                                        </motion.div>
                                    ))}
                                </AnimatePresence>
                            </Box>
                        ))
                    )}
                    <div ref={messagesEndRef} />
                </Box>
            </DialogContent>

            {/* Input Area */}
            <DialogActions
                sx={{
                    p: 2,
                    borderTop: '1px solid',
                    borderColor: 'divider',
                    bgcolor: 'background.paper'
                }}
            >
                <Box sx={{ display: 'flex', width: '100%', gap: 1, alignItems: 'flex-end' }}>
                    <input id="p2p-chat-image" type="file" accept="image/*" style={{ display: 'none' }} onChange={handleAttachImage} />
                    <label htmlFor="p2p-chat-image">
                        <IconButton component="span">
                            <AttachFile />
                        </IconButton>
                    </label>
                    <TextField
                        fullWidth
                        multiline
                        maxRows={4}
                        placeholder="Type your message..."
                        value={newMessage}
                        onChange={(e) => setNewMessage(e.target.value)}
                        onKeyPress={handleKeyPress}
                        disabled={sending}
                        size="small"
                        sx={{
                            '& .MuiOutlinedInput-root': {
                                borderRadius: 3,
                                bgcolor: theme.palette.mode === 'dark' ? 'grey.800' : 'grey.100'
                            }
                        }}
                    />
                    <IconButton
                        color="primary"
                        onClick={sendMessage}
                        disabled={!newMessage.trim() || sending}
                        sx={{
                            bgcolor: 'primary.main',
                            color: 'white',
                            '&:hover': {
                                bgcolor: 'primary.dark'
                            },
                            '&:disabled': {
                                bgcolor: 'action.disabledBackground'
                            }
                        }}
                    >
                        {sending ? (
                            <CircularProgress size={20} color="inherit" />
                        ) : (
                            <Send />
                        )}
                    </IconButton>
                </Box>
            </DialogActions>
        </Dialog>
    );
};

export default P2PChatDialog;
